import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ModelBuilderService } from '../services/modelBuilderService';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import AssignmentRuleEditor from './AssignmentRuleEditor';
import { GripVertical, Plus, Trash2, Edit } from 'lucide-react';
import { Button, ConfirmDialog } from './ui';

// Sortable Rule Row Component
const SortableRuleRow = ({ rule, modelBuilders, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'var(--dark-elevated)' : 'var(--dark-bg)',
    border: '1px solid var(--gray-600)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-3)',
    marginBottom: 'var(--space-2)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
  };

  // Get model builder names from IDs
  const getBuilderNames = () => {
    if (!rule.eligible_model_builder_ids || rule.eligible_model_builder_ids.length === 0) {
      return [];
    }
    return rule.eligible_model_builder_ids
      .map(id => {
        const builder = modelBuilders.find(b => b.id === id);
        return builder ? builder.name.split(' ')[0] : null; // First name only
      })
      .filter(Boolean);
  };

  const builderNames = getBuilderNames();

  // Badge colors
  const getBuilderColor = (name) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('adam')) return '#3b82f6';
    if (lowerName.includes('zach')) return '#8b5cf6';
    if (lowerName.includes('stef')) return '#f59e0b';
    return '#6b7280';
  };

  return (
    <div ref={setNodeRef} style={style}>
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        style={{
          cursor: 'grab',
          color: 'var(--gray-400)',
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
        }}
      >
        <GripVertical size={20} />
      </div>

      {/* Priority Number */}
      <div
        style={{
          minWidth: '30px',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--gray-400)',
        }}
      >
        #{rule.priority}
      </div>

      {/* Rule Name */}
      <div
        style={{
          flex: 1,
          fontSize: 'var(--text-sm)',
          color: 'var(--gray-200)',
        }}
      >
        {rule.rule_name}
      </div>

      {/* Eligible Builders */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        {builderNames.map((name, index) => (
          <span
            key={index}
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-medium)',
              color: '#ffffff',
              backgroundColor: getBuilderColor(name),
              padding: '4px 8px',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {name}
          </span>
        ))}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
        }}
      >
        <button
          onClick={() => onEdit(rule)}
          style={{
            background: 'var(--dark-elevated)',
            border: '1px solid var(--gray-600)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-2)',
            color: 'var(--gray-300)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            transition: 'all var(--transition-fast)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--cta-blue)';
            e.currentTarget.style.color = 'var(--cta-blue)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--gray-600)';
            e.currentTarget.style.color = 'var(--gray-300)';
          }}
          title="Edit rule"
        >
          <Edit size={16} />
        </button>
        <button
          onClick={() => onDelete(rule)}
          style={{
            background: 'var(--dark-elevated)',
            border: '1px solid var(--gray-600)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-2)',
            color: 'var(--gray-300)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            transition: 'all var(--transition-fast)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--danger)';
            e.currentTarget.style.color = 'var(--danger)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--gray-600)';
            e.currentTarget.style.color = 'var(--gray-300)';
          }}
          title="Delete rule"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const AssignmentRulesManager = () => {
  const [rules, setRules] = useState([]);
  const [modelBuilders, setModelBuilders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rulesData, buildersData] = await Promise.all([
        ModelBuilderService.getAssignmentRules(),
        ModelBuilderService.getModelBuilders()
      ]);

      setRules(rulesData);
      setModelBuilders(buildersData);
    } catch (err) {
      console.error('Error loading assignment rules:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = rules.findIndex(r => r.id === active.id);
    const newIndex = rules.findIndex(r => r.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update UI
    const newRules = arrayMove(rules, oldIndex, newIndex);
    setRules(newRules);

    // Update priorities in database
    try {
      await ModelBuilderService.reorderRules(newRules);
    } catch (err) {
      console.error('Error reordering rules:', err);
      // Revert on error
      loadData();
      alert('Failed to reorder rules. Please try again.');
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setEditorOpen(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setEditorOpen(true);
  };

  const handleDeleteClick = (rule) => {
    setRuleToDelete(rule);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;

    try {
      await ModelBuilderService.deleteAssignmentRule(ruleToDelete.id);
      setDeleteConfirmOpen(false);
      setRuleToDelete(null);
      loadData();
    } catch (err) {
      console.error('Error deleting rule:', err);
      alert('Failed to delete rule. Please try again.');
    }
  };

  const handleEditorSave = async () => {
    setEditorOpen(false);
    setEditingRule(null);
    loadData();
  };

  const handleEditorCancel = () => {
    setEditorOpen(false);
    setEditingRule(null);
  };

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      {/* Header */}
      <div
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
              margin: 0,
              marginBottom: 'var(--space-2)',
            }}
          >
            Model Builder Assignment Rules
          </h1>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
              margin: 0,
            }}
          >
            Rules are evaluated in order from top to bottom. Higher rules take priority.
          </p>
        </div>
        <Button onClick={handleAddRule} variant="primary">
          <Plus size={16} />
          Add Rule
        </Button>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <div
          style={{
            padding: 'var(--space-8)',
            textAlign: 'center',
            color: 'var(--gray-400)',
            backgroundColor: 'var(--dark-elevated)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--gray-600)',
          }}
        >
          <p style={{ fontSize: 'var(--text-lg)', margin: 0 }}>
            No assignment rules configured yet.
          </p>
          <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>
            Click "Add Rule" to create your first assignment rule.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={rules.map(r => r.id)}
            strategy={verticalListSortingStrategy}
          >
            {rules.map((rule) => (
              <SortableRuleRow
                key={rule.id}
                rule={rule}
                modelBuilders={modelBuilders}
                onEdit={handleEditRule}
                onDelete={handleDeleteClick}
              />
            ))}
          </SortableContext>

          <DragOverlay>
            {activeId ? (
              <div
                style={{
                  backgroundColor: 'var(--dark-elevated)',
                  border: '2px solid var(--cta-blue)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  opacity: 0.9,
                }}
              >
                Reordering...
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Rule Editor Modal */}
      {editorOpen && (
        <AssignmentRuleEditor
          isOpen={editorOpen}
          rule={editingRule}
          modelBuilders={modelBuilders}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Assignment Rule"
        message={`Are you sure you want to delete the rule "${ruleToDelete?.rule_name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setRuleToDelete(null);
        }}
        variant="danger"
      />
    </div>
  );
};

export default AssignmentRulesManager;
